import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, Calendar, Clock, User, FileText, CheckCircle, 
  Search, Bell, MoreVertical, Mic, PlayCircle, Shield, 
  X, ChevronRight, MessageSquare, AlertCircle, Play
} from 'lucide-react';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Card } from '../../ui/card';
import { Separator } from '../../ui/separator';
import { Dialog, DialogContent, DialogTrigger } from '../../ui/dialog';

const AIReceptionistSlide = () => {
  const [selectedCallId, setSelectedCallId] = useState("call-dental-123");

  const selectedCall = dentalReceptionistData.recentCalls.find(c => c.id === selectedCallId);

  return (
    <div className="min-h-full xl:h-full w-full bg-slate-100 flex items-center justify-center p-4 xl:p-12 relative overflow-y-auto xl:overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-100/50 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />

        {/* Floating Agent Header (Top Left) */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                 <img src="/assets/amy.png" alt="Amy" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-slate-900 leading-none">Amy</h3>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">AI Receptionist</p>
            </div>
        </div>

        <div className="flex flex-col xl:flex-row h-full w-full max-w-[1600px] gap-8 relative z-10 pt-16 xl:pt-0">
            {/* Left Side: Context */}
            <div className="w-full xl:w-2/5 flex flex-col justify-center h-auto xl:h-full py-4 xl:py-8 pr-0 xl:pr-4">
                
                <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
                    The Industry's Most <span className="text-blue-600">Advanced AI Receptionist</span>.
                </h2>

                <p className="text-lg font-bold text-slate-700 mb-8 max-w-xl">
                    More Features and Configurations Than Any AI Receptionist in the Industry.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                    {[
                        "24/7 call answering",
                        "Multiple languages",
                        "Unlimited calls per location",
                        "Advanced transfer rules",
                        "Unlimited providers",
                        "Unlimited advanced scheduling rules",
                        "Trained on 7,300+ dental calls",
                        "Fully HIPAA compliant",
                        "Books directly into PMS",
                        "110+ customization settings",
                        "Instant call notifications",
                        "Detailed call summaries",
                        "Full transcripts",
                        "Audio recordings",
                        "Advanced analytics portal",
                        "Operatory-specific logic",
                        "Insurance collection",
                        "New patient intake"
                    ].map((feature, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                            <div className="mt-0.5 bg-blue-100 p-0.5 rounded-full">
                                <CheckCircle className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                            </div>
                            <span className="text-sm font-semibold text-slate-700 leading-tight">{feature}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right Side: Browser Mockup */}
            <div className="w-full xl:w-3/5 h-auto xl:h-full flex items-center justify-center pb-8 xl:pb-0">
                <motion.div 
                    initial={{ y: 40, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="w-full xl:w-[90%] h-[500px] xl:h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
                >
                    {/* Browser Toolbar */}
                    <div className="h-12 bg-slate-50 border-b border-slate-200 flex items-center px-4 space-x-4 flex-shrink-0">
                        <div className="flex space-x-2">
                            <div className="w-3 h-3 rounded-full bg-red-400" />
                            <div className="w-3 h-3 rounded-full bg-yellow-400" />
                            <div className="w-3 h-3 rounded-full bg-green-400" />
                        </div>
                        <div className="flex-1 bg-white border border-slate-200 rounded-md h-8 flex items-center px-3 text-xs text-slate-400 font-mono">
                            <Shield className="w-3 h-3 mr-2 text-green-500" />
                            app.ashahealth.com/dashboard/receptionist
                        </div>
                    </div>

                    {/* App Content */}
                    <div className="flex-1 flex overflow-hidden relative">
                        {/* Demo Button Overlay */}
                        <div className="absolute bottom-6 right-6 z-50 flex items-center gap-4">
                            {/* Audio Wave Animation */}
                            <div className="flex items-center gap-1 h-8">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <motion.div
                                        key={i}
                                        animate={{
                                            height: [8, 24, 8],
                                            opacity: [0.5, 1, 0.5]
                                        }}
                                        transition={{
                                            duration: 1,
                                            repeat: Infinity,
                                            delay: i * 0.1,
                                            ease: "easeInOut"
                                        }}
                                        className="w-1 bg-blue-500 rounded-full"
                                    />
                                ))}
                            </div>

                            <Dialog>
                                <DialogTrigger asChild>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="group relative inline-flex items-center gap-3 bg-slate-900/90 backdrop-blur-md px-5 py-3 rounded-full shadow-2xl border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300"
                                    >
                                        <div className="relative flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                                            <Play className="w-3.5 h-3.5 ml-0.5 fill-current" />
                                            <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20" />
                                        </div>
                                        <div className="text-left pr-1">
                                            <div className="text-xs font-bold text-white">Watch Live Demo</div>
                                            <div className="text-[10px] text-slate-300">2 min video</div>
                                        </div>
                                    </motion.button>
                                </DialogTrigger>
                                <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-none">
                                    <div className="relative w-full pt-[62.5%]">
                                        <iframe 
                                            src="https://www.loom.com/embed/28bf483dd2a3443aafe8bee3a983fa2e" 
                                            frameBorder="0" 
                                            webkitallowfullscreen="true" 
                                            mozallowfullscreen="true" 
                                            allowFullScreen 
                                            className="absolute top-0 left-0 w-full h-full"
                                        ></iframe>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>

                        {/* Main Content: Call Detail */}
                        <div className="flex-1 bg-white flex flex-col h-full overflow-hidden">
                            {selectedCall ? (
                                <div className="flex flex-col h-full">
                                    {/* Header */}
                                    <div className="p-6 border-b border-slate-100 bg-white flex-shrink-0">
                                        <div className="flex justify-between items-start mb-6">
                                            <div>
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h2 className="text-xl font-bold text-slate-900">{selectedCall.patientName}</h2>
                                                    <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">Verified Patient</Badge>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                                    <Phone className="w-3 h-3" /> 
                                                    {selectedCall.phoneNumber}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors">
                                                    <MoreVertical className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Summary Card - Moved Above */}
                                        <div className="bg-blue-50/50 rounded-xl p-5 mb-6 border border-blue-100">
                                            <h3 className="text-xs font-bold uppercase tracking-wider text-blue-800 mb-3 flex items-center">
                                                <FileText className="w-3 h-3 mr-2" /> Call Summary
                                            </h3>
                                            <p className="text-sm text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: selectedCall.summary }}></p>
                                            <div className="mt-4 flex gap-2">
                                                <Badge className="bg-white hover:bg-white text-slate-600 border border-slate-200 shadow-sm">
                                                    <CheckCircle className="w-3 h-3 mr-1 text-green-500" /> 
                                                    PMS Updated
                                                </Badge>
                                                <Badge className="bg-white hover:bg-white text-slate-600 border border-slate-200 shadow-sm">
                                                    <Calendar className="w-3 h-3 mr-1 text-blue-500" /> 
                                                    Appt Booked
                                                </Badge>
                                            </div>
                                        </div>

                                        {/* Structured Patient Information - Moved Below */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                                                <div className="text-xs text-slate-500 font-medium mb-1">Date of Birth</div>
                                                <div className="text-sm font-semibold text-slate-900">{selectedCall.dateOfBirth || "1985-06-15"}</div>
                                            </div>
                                            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                                                <div className="text-xs text-slate-500 font-medium mb-1">Insurance</div>
                                                <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                                                    <Shield className="w-3 h-3 text-blue-500" />
                                                    {selectedCall.insurance}
                                                </div>
                                            </div>
                                            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                                                <div className="text-xs text-slate-500 font-medium mb-1">Last Visit</div>
                                                <div className="text-sm font-semibold text-slate-900">Nov 12, 2023</div>
                                            </div>
                                            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                                                <div className="text-xs text-slate-500 font-medium mb-1">Balance</div>
                                                <div className="text-sm font-semibold text-slate-900">$0.00</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-6 pt-0">
                                        {/* Transcript */}
                                        <div>
                                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center">
                                                <MessageSquare className="w-3 h-3 mr-2" /> Live Transcript
                                            </h3>
                                            <div className="space-y-4">
                                                {transcripts[selectedCall.id]?.map((msg, idx) => (
                                                    <div key={idx} className={`flex gap-4 ${msg.role === 'ai' ? 'flex-row' : 'flex-row-reverse'}`}>
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                            msg.role === 'ai' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                                                        }`}>
                                                            {msg.role === 'ai' ? <Mic className="w-4 h-4" /> : <User className="w-4 h-4" />}
                                                        </div>
                                                        <div className={`flex-1 p-3 rounded-2xl text-sm leading-relaxed max-w-[85%] ${
                                                            msg.role === 'ai' 
                                                                ? 'bg-slate-50 text-slate-800 rounded-tl-none' 
                                                                : 'bg-blue-600 text-white rounded-tr-none shadow-sm'
                                                        }`}>
                                                            {msg.text}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                                    <Search className="w-12 h-12 mb-4 opacity-20" />
                                    <p>Select a call to view details</p>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </div>
  );
};

// Data
const dentalReceptionistData = {
  recentCalls: [
    {
      id: "call-dental-123",
      description: "Michelle Johnson scheduled new patient exam after hours.",
      callType: "New Patient",
      timestamp: "2024-01-15T19:30:00",
      patientName: "Michelle Johnson",
      phoneNumber: "(555) 123-4567",
      insurance: "Delta Dental",
      duration: "4:32",
      summary: "Michelle Johnson called while the office was closed to schedule a new patient exam. I successfully booked her for an open slot that was available later today at 4:00 PM, <b>capturing a new patient opportunity</b> that would have otherwise been <b>missed</b>."
    },
    {
      id: "call-dental-124",
      description: "David Brown called about tooth pain and was scheduled for emergency appointment.",
      callType: "Emergency",
      timestamp: "2024-01-15T13:45:00",
      patientName: "David Brown",
      phoneNumber: "(555) 234-5678",
      insurance: "MetLife Dental",
      duration: "3:18",
      summary: "David Brown called with severe tooth pain. Emergency appointment scheduled for today at 4:30 PM with Dr. Martinez. Pre-authorized emergency visit with MetLife Dental."
    },
    {
      id: "call-dental-125",
      description: "Susan Williams scheduled follow-up appointment for crown placement procedure.",
      callType: "Scheduling",
      timestamp: "2024-01-15T11:20:00",
      patientName: "Susan Williams",
      phoneNumber: "(555) 456-7890",
      insurance: "Cigna Dental",
      duration: "2:47",
      summary: "Susan Williams called to schedule her crown placement follow-up. Appointment scheduled for next Monday at 10:00 AM with Dr. Davis. Verified Cigna Dental coverage for the procedure."
    },
    {
      id: "call-dental-126",
      description: "Robert Chen cancelled his appointment and rescheduled for next week due to work conflict.",
      callType: "Scheduling",
      timestamp: "2024-01-15T09:30:00",
      patientName: "Robert Chen",
      phoneNumber: "(555) 678-9012",
      insurance: "Humana Dental",
      duration: "2:15",
      summary: "Robert Chen cancelled his cleaning appointment due to work emergency. Rescheduled for next Wednesday at 11:00 AM with the same hygienist."
    },
    {
      id: "call-dental-127",
      description: "Maria Garcia scheduled initial consultation for orthodontic treatment.",
      callType: "Consultation",
      timestamp: "2024-01-14T16:45:00",
      patientName: "Maria Garcia",
      phoneNumber: "(555) 890-1234",
      insurance: "Guardian Dental",
      duration: "5:33",
      summary: "Maria Garcia called for orthodontic consultation. Initial appointment scheduled with Dr. Thompson for next Friday at 3:00 PM. Discussed treatment options and insurance coverage with Guardian Dental."
    }
  ]
};

const transcripts = {
  "call-dental-123": [
    { role: "ai", text: "Thank you for calling Dr. Smith's office, this is Amy. How can I help you?" },
    { role: "patient", text: "Hi, I'd like to schedule an exam. Are you open?" },
    { role: "ai", text: "Our office is currently closed, but I can help you schedule right now. Are you a new patient?" },
    { role: "patient", text: "Yes, I'm new. My name is Michelle Johnson." },
    { role: "ai", text: "Welcome Michelle. I see we actually have a last-minute opening later today at 4:00 PM due to a cancellation. Would you like to grab that?" },
    { role: "patient", text: "Oh really? That would be amazing actually." },
    { role: "ai", text: "Great. I'll book you for the New Patient Exam at 4:00 PM today. Do you have dental insurance?" },
    { role: "patient", text: "Yes, Delta Dental." },
    { role: "ai", text: "Perfect. I've secured that slot for you. We'll see you at 4:00 PM today!" }
  ],
  "call-dental-124": [
    { role: "ai", text: "Thank you for calling Dr. Smith's office, this is Amy. How can I help you?" },
    { role: "patient", text: "I have a really bad toothache, it's throbbing." },
    { role: "ai", text: "I'm sorry to hear that. Since you're in pain, we can treat this as an emergency. Can you come in today at 4:30 PM?" },
    { role: "patient", text: "Yes, please. I really need to see someone." },
    { role: "ai", text: "Okay, I've booked you with Dr. Martinez at 4:30 PM today. I'll also verify your MetLife Dental coverage." },
    { role: "patient", text: "Thank you so much." }
  ],
  "call-dental-125": [
    { role: "ai", text: "Thank you for calling Dr. Smith's office, this is Amy. How can I help you?" },
    { role: "patient", text: "Hi, I need to schedule a follow-up for my crown placement." },
    { role: "ai", text: "I can assist you with that. Can I have your name and date of birth?" },
    { role: "patient", text: "Susan Williams, November 8th, 1973." },
    { role: "ai", text: "Thank you Susan. For the crown follow-up, Dr. Davis usually sees patients one week after the procedure. How does next Monday at 10:00 AM look?" },
    { role: "patient", text: "That works for me." },
    { role: "ai", text: "Perfect. I've scheduled you for next Monday at 10:00 AM. We'll see you then!" }
  ],
  "call-dental-126": [
    { role: "ai", text: "Thank you for calling Dr. Smith's office, this is Amy. How can I help you?" },
    { role: "patient", text: "Hi, this is Robert Chen. I have an appointment tomorrow but I have to cancel." },
    { role: "ai", text: "I understand, Robert. I can help you reschedule. When would you like to come in instead?" },
    { role: "patient", text: "Maybe next week? Wednesday morning?" },
    { role: "ai", text: "Let me check. Yes, I have an 11:00 AM on Wednesday next week. Would you like to grab that?" },
    { role: "patient", text: "Yes, please." },
    { role: "ai", text: "Done. Your appointment is moved to next Wednesday at 11:00 AM." }
  ],
  "call-dental-127": [
    { role: "ai", text: "Thank you for calling Dr. Smith's office, this is Amy. How can I help you?" },
    { role: "patient", text: "I'm interested in getting braces or Invisalign. Do you do consultations?" },
    { role: "ai", text: "Yes, we do offering orthodontic consultations. Are you a new patient?" },
    { role: "patient", text: "Yes, my name is Maria Garcia." },
    { role: "ai", text: "Welcome Maria. I can schedule a consultation with Dr. Thompson. How is next Friday at 3:00 PM?" },
    { role: "patient", text: "That works. Do you take Guardian Dental?" },
    { role: "ai", text: "Yes we do. I'll note that in your file. I've booked your consultation for next Friday at 3:00 PM." }
  ]
};

export default AIReceptionistSlide;

